import React, { useState, useEffect } from 'react';
import { DataGrid, GridActionsCellItem, GridColDef } from '@mui/x-data-grid';
import { apiUrl } from '../utils/api';
import {
  Button,
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Alert,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  AlertColor,
  Autocomplete,
  TextField,
  CircularProgress
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import CompileIcon from '@mui/icons-material/Build';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import HandymanIcon from '@mui/icons-material/Handyman';

const ContractDesignChanges = ({ access = 'none', projectId = null, deleteAllAccess = 'none', userId = null }) => {
  const readOnly = access !== 'write';
  const canDeleteAll = deleteAllAccess === 'write';
  const [contractDesignChanges, setContractDesignChanges] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [designChanges, setDesignChanges] = useState([]);
  const [dcBoqLines, setDcBoqLines] = useState([]);
  const [budgetLineItems, setBudgetLineItems] = useState([]);
  const [units, setUnits] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingContracts, setLoadingContracts] = useState(false);
  const [loadingDesignChanges, setLoadingDesignChanges] = useState(false);
  const [linkingLoading, setLinkingLoading] = useState(false);
  const [newRelationship, setNewRelationship] = useState({
    contract_id: '',
    change_number: ''
  });
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [selectedDC, setSelectedDC] = useState(null);
  const [compileDialogOpen, setCompileDialogOpen] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Excel Import/Export States
  const [excelImporting, setExcelImporting] = useState(false);
  const [excelExporting, setExcelExporting] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importResults, setImportResults] = useState<any>(null);
  const [currentImportFile, setCurrentImportFile] = useState<File | null>(null);

  // Delete All Dialog States
  const [deleteAllDialogOpen, setDeleteAllDialogOpen] = useState(false);
  const [deletingAll, setDeletingAll] = useState(false);

  const showNotification = (message, severity = 'success') => {
    setNotification({ open: true, message, severity });
  };

  const fetchContractDesignChanges = async () => {
    if (access === 'none' || !projectId) return;

    setLoading(true);
    try {
      const response = await fetch(apiUrl(`/api/contract-design-changes?project_id=${projectId}`));
      if (!response.ok) throw new Error('Failed to fetch contract design changes');

      const data = await response.json();
      setContractDesignChanges(data);
    } catch (error) {
      console.error('Error fetching contract design changes:', error);
      showNotification('Failed to load contract design changes', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchContracts = async () => {
    if (!projectId) return;

    console.log('🔵 ContractDesignChanges - Fetching contracts for project_id:', projectId);
    setLoadingContracts(true);

    try {
      const response = await fetch(apiUrl(`/api/contracts?project_id=${projectId}`));
      if (!response.ok) throw new Error('Failed to fetch contracts');

      const data = await response.json();
      console.log('🔵 ContractDesignChanges - Received contracts:', data.length, 'contracts');

      // Filter by project_id on frontend (in case backend returns extra data)
      const filteredData = data.filter(c => c.project_id === projectId);
      console.log('🔵 ContractDesignChanges - Filtered to:', filteredData.length, 'contracts for project', projectId);
      console.log('🔵 ContractDesignChanges - Contract numbers:', filteredData.map(c => c.contract_number));

      setContracts(filteredData);
    } catch (error) {
      console.error('Error fetching contracts:', error);
    } finally {
      setLoadingContracts(false);
    }
  };

  const fetchDesignChanges = async () => {
    if (!projectId) return;

    setLoadingDesignChanges(true);
    try {
      const response = await fetch(apiUrl(`/api/design-changes?project_id=${projectId}`));
      if (!response.ok) throw new Error('Failed to fetch design changes');

      const data = await response.json();
      setDesignChanges(data);
    } catch (error) {
      console.error('Error fetching design changes:', error);
    } finally {
      setLoadingDesignChanges(false);
    }
  };

  const fetchDCBoqLines = async (changeNumber) => {
    if (!changeNumber) return;
    
    try {
      const response = await fetch(apiUrl(`/api/design-changes/${changeNumber}/boq`));
      if (!response.ok) throw new Error('Failed to fetch DC BOQ lines');
      
      const data = await response.json();
      setDcBoqLines(data);
    } catch (error) {
      console.error('Error fetching DC BOQ lines:', error);
    }
  };

  const fetchBudgetLineItems = async () => {
    try {
      let url = apiUrl('/api/budget-line-items');
      if (projectId) {
        url += `?project_id=${projectId}`;
      }
      
      const response = await fetch(url);
      if (!response.ok) throw new Error('Failed to fetch budget line items');
      
      const data = await response.json();
      setBudgetLineItems(data);
    } catch (error) {
      console.error('Error fetching budget line items:', error);
    }
  };

  const fetchUnits = async () => {
    try {
      const response = await fetch(apiUrl('/api/units'));
      if (!response.ok) throw new Error('Failed to fetch units');

      const data = await response.json();
      setUnits(data);
    } catch (error) {
      console.error('Error fetching units:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await fetch(apiUrl('/api/users'));
      if (!response.ok) throw new Error('Failed to fetch users');

      const data = await response.json();
      setUsers(data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  useEffect(() => {
    fetchContractDesignChanges();
    fetchContracts();
    fetchDesignChanges();
    fetchBudgetLineItems();
    fetchUnits();
    fetchUsers();
  }, [access, projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAdd = async () => {
    if (readOnly || !newRelationship.contract_id || !newRelationship.change_number) return;

    setLinkingLoading(true);
    try {
      const response = await fetch(apiUrl('/api/contract-design-changes'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newRelationship)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create relationship');
      }

      const result = await response.json();

      setNewRelationship({
        contract_id: '',
        change_number: ''
      });
      fetchContractDesignChanges();

      // Show success message with BOQ import details
      if (result.message) {
        showNotification(result.message);
      } else {
        showNotification('DC linked to contract successfully');
      }
    } catch (error) {
      console.error('Error creating relationship:', error);
      showNotification(error.message, 'error');
    } finally {
      setLinkingLoading(false);
    }
  };

  const handleDelete = (id) => () => {
    if (readOnly) return;
    const relationship = contractDesignChanges.find(r => r.id === id);
    setItemToDelete(relationship);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!itemToDelete) return;

    setDeleting(true);
    try {
      const response = await fetch(apiUrl(`/api/contract-design-changes/${itemToDelete.id}`), {
        method: 'DELETE'
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete relationship');
      }

      fetchContractDesignChanges();
      showNotification('Relationship deleted successfully');
      setDeleteDialogOpen(false);
      setItemToDelete(null);
    } catch (error) {
      console.error('Error deleting relationship:', error);
      showNotification(error.message, 'error');
    } finally {
      setDeleting(false);
    }
  };

  const handlePreviewDC = async (changeNumber) => {
    setSelectedDC(changeNumber);
    await fetchDCBoqLines(changeNumber);
    setPreviewDialogOpen(true);
  };

  const handleCompileDC = async (contractId, changeNumber) => {
    if (readOnly) return;
    
    try {
      const response = await fetch(apiUrl(`/api/contracts/${contractId}/compile-dc-boq`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ change_number: changeNumber })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to compile DC BOQ');
      }
      
      const result = await response.json();
      showNotification(result.message);
      setCompileDialogOpen(false);
    } catch (error) {
      console.error('Error compiling DC BOQ:', error);
      showNotification(error.message, 'error');
    }
  };

  const getBudgetLineItem = (bliId) => {
    return budgetLineItems.find(bli => bli.bli_id === bliId);
  };

  const getUnit = (unitId) => {
    return units.find(u => u.unit_id === unitId);
  };

  const getContractName = (contractId) => {
    const contract = contracts.find(c => c.contract_id === contractId);
    return contract ? `${contract.contract_number}${contract.title ? ' - ' + contract.title : ''}` : 'Unknown';
  };

  const getDesignChangeTitle = (changeNumber) => {
    const dc = designChanges.find(dc => dc.change_number === changeNumber);
    return dc ? (dc.change_name || dc.title || 'No Title') : 'Unknown';
  };

  // Excel Template Download Handler
  const handleDownloadTemplate = () => {
    fetch(apiUrl('/api/contract-design-changes/template'))
      .then(res => res.blob())
      .then(blob => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'contract_design_changes_template.xlsx';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
      })
      .catch(err => {
        console.error('Error downloading template:', err);
        showNotification('Failed to download template', 'error');
      });
  };

  // Excel Export Handler
  const handleExportExcel = async () => {
    try {
      setExcelExporting(true);
      const url = projectId
        ? apiUrl(`/api/contract-design-changes/export?project_id=${projectId}`)
        : apiUrl('/api/contract-design-changes/export');

      const response = await fetch(url, {
        credentials: 'include'
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to export contract design changes');
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = 'contract_design_changes.xlsx';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);

      showNotification('Export successful');
    } catch (err: any) {
      showNotification(err.message, 'error');
    } finally {
      setExcelExporting(false);
    }
  };

  // Excel Import Handler
  const handleImportExcel = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Reset file input immediately
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    try {
      setExcelImporting(true);
      const formData = new FormData();
      formData.append('file', file);

      const url = projectId
        ? apiUrl(`/api/contract-design-changes/import?project_id=${projectId}`)
        : apiUrl('/api/contract-design-changes/import');

      const response = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to import contract design changes');
      }

      // Store results and show dialog
      setImportResults(data);
      setCurrentImportFile(file);
      setImportDialogOpen(true);

      // Refresh contract design changes list
      fetchContractDesignChanges();
    } catch (err: any) {
      showNotification(err.message, 'error');
    } finally {
      setExcelImporting(false);
    }
  };

  const handleImportDialogClose = () => {
    setImportDialogOpen(false);
    setImportResults(null);
    setCurrentImportFile(null);
  };

  // Delete All handlers
  const handleDeleteAllClick = () => {
    if (!canDeleteAll) return;
    setDeleteAllDialogOpen(true);
  };

  const handleDeleteAllConfirm = async () => {
    if (!projectId) return;

    setDeletingAll(true);
    try {
      const response = await fetch(apiUrl(`/api/contract-design-changes/delete-all?project_id=${projectId}`), {
        method: 'DELETE',
        headers: { ...(userId ? { 'X-User-ID': userId } : {}) },
      });

      if (response.ok) {
        fetchContractDesignChanges();
        showNotification('All contract-design-change links deleted successfully', 'success');
        setDeleteAllDialogOpen(false);
      } else {
        const error = await response.json();
        showNotification(error.error || 'Failed to delete all links', 'error');
      }
    } catch (error) {
      showNotification('Failed to delete all links', 'error');
    } finally {
      setDeletingAll(false);
    }
  };

  const handleDeleteAllCancel = () => {
    setDeleteAllDialogOpen(false);
  };

  const getUserEmail = (userId) => {
    if (!userId) return userId || '';
    const user = users.find(u => u.user_id === parseInt(userId));
    return user ? (user.user_email || user.username || userId) : userId;
  };

  const columns: GridColDef[] = [
    {
      field: 'id',
      headerName: 'ID',
      width: 90,
      type: 'number'
    },
    {
      field: 'contract_title',
      headerName: 'Contract',
      flex: 2,
      minWidth: 200
    },
    {
      field: 'change_number',
      headerName: 'Design Change',
      flex: 1,
      minWidth: 150
    },
    {
      field: 'change_name',
      headerName: 'DC Title',
      flex: 2,
      minWidth: 200
    },
    {
      field: 'compiled_at',
      headerName: 'Compiled At',
      width: 200
    },
    // {
    //   field: 'compiled_by',
    //   headerName: 'Compiled By',
    //   width: 150
    // },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 200,
      getActions: ({ row }) => {
        const actions = [
          <GridActionsCellItem
            icon={<VisibilityIcon />}
            label="Preview BOQ"
            onClick={() => handlePreviewDC(row.change_number)}
            showInMenu={false}
          />
        ];

        if (!readOnly) {
          actions.push(
            <GridActionsCellItem
              icon={<CompileIcon />}
              label="Compile to Contract"
              onClick={() => {
                setSelectedContract(row.contract_id);
                setSelectedDC(row.change_number);
                setCompileDialogOpen(true);
              }}
              showInMenu={false}
            />,
            <GridActionsCellItem
              icon={<DeleteIcon />}
              label="Delete"
              onClick={handleDelete(row.id)}
              showInMenu={false}
            />
          );
        }
        
        return actions;
      }
    }
  ];

  if (access === 'none') {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          You don't have permission to access this page.
        </Alert>
      </Box>
    );
  }

  if (!projectId) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">
          Please select a project to manage contract design changes.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Link design changes to contracts and compile their BOQ lines into contract budget lines.
        This creates a complete audit trail from design changes to contract costs.
      </Typography>

      {!readOnly && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Link Design Change to Contract
            </Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid size={{ xs: 12, sm: 4 }}>
                <Autocomplete
                  fullWidth
                  size="small"
                  options={contracts}
                  loading={loadingContracts}
                  getOptionLabel={(option) => `${option.contract_number}${option.title ? ` - ${option.title}` : ''}`}
                  value={contracts.find(c => c.contract_id === newRelationship.contract_id) || null}
                  onChange={(event, newValue) => {
                    setNewRelationship({ ...newRelationship, contract_id: newValue ? newValue.contract_id : '' });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Contract / VO"
                      placeholder="Search and select contract or VO..."
                    />
                  )}
                  noOptionsText="No contracts available"
                  loadingText="Loading Contracts and VO ..."
                  isOptionEqualToValue={(option, value) => option.contract_id === value.contract_id}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <Autocomplete
                  fullWidth
                  size="small"
                  options={designChanges}
                  loading={loadingDesignChanges}
                  getOptionLabel={(option) => `${option.change_number}${(option.change_name || option.title) ? ` - ${option.change_name || option.title}` : ''}`}
                  value={designChanges.find(dc => dc.change_number === newRelationship.change_number) || null}
                  onChange={(event, newValue) => {
                    setNewRelationship({ ...newRelationship, change_number: newValue ? newValue.change_number : '' });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Design Change"
                      placeholder="Search and select design change..."
                    />
                  )}
                  noOptionsText="No design changes available"
                  loadingText="Loading design changes..."
                  isOptionEqualToValue={(option, value) => option.change_number === value.change_number}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleAdd}
                  disabled={!newRelationship.contract_id || !newRelationship.change_number || linkingLoading}
                  startIcon={linkingLoading ? <CircularProgress size={20} color="inherit" /> : <AddIcon />}
                >
                  {linkingLoading ? 'Linking...' : 'Link DC to Contract and VO'}
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent sx={{ p: 0 }}>
          <DataGrid
            rows={contractDesignChanges}
            columns={columns}
            getRowId={(row) => row.id}
            loading={loading}
            disableRowSelectionOnClick
            autoHeight
            initialState={{
              pagination: {
                paginationModel: { page: 0, pageSize: 25 }
              }
            }}
            pageSizeOptions={[25, 50, 100]}
            sx={{
              border: 0,
              '& .MuiDataGrid-cell:focus': {
                outline: 'none'
              },
              '& .MuiDataGrid-row:hover': {
                backgroundColor: 'action.hover'
              }
            }}
          />
        </CardContent>
      </Card>

      {/* Import/Export Buttons */}
      <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Button variant="outlined" onClick={handleDownloadTemplate}>
          Export Template
        </Button>
        <Button
          variant="contained"
          onClick={handleExportExcel}
          disabled={excelExporting || contractDesignChanges.length === 0}
        >
          {excelExporting ? 'Exporting...' : 'Export To Excel'}
        </Button>
        <Button
          variant="contained"
          onClick={() => fileInputRef.current?.click()}
          disabled={excelImporting || readOnly}
        >
          {excelImporting ? 'Importing...' : 'Import From Excel'}
        </Button>
        {canDeleteAll && (
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteAllClick}
            disabled={deletingAll || loading}
          >
            {deletingAll ? 'Deleting...' : 'Delete All'}
          </Button>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xls"
          onChange={handleImportExcel}
          style={{ display: 'none' }}
        />
      </Box>

      {/* Preview BOQ Dialog */}
      <Dialog open={previewDialogOpen} onClose={() => setPreviewDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          BOQ Preview - Design Change {selectedDC}
          <IconButton
            sx={{ position: 'absolute', right: 8, top: 8 }}
            onClick={() => setPreviewDialogOpen(false)}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="h6" gutterBottom>
            {getDesignChangeTitle(selectedDC)}
          </Typography>
          
          {dcBoqLines.length === 0 ? (
            <Alert severity="info">
              No BOQ lines found for this design change.
            </Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Line #</TableCell>
                    <TableCell>Item Code</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Unit</TableCell>
                    <TableCell align="right">Qty</TableCell>
                    <TableCell align="right">Unit Price</TableCell>
                    <TableCell align="right">Total</TableCell>
                    <TableCell>Notes</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dcBoqLines.map((line) => {
                    const bli = getBudgetLineItem(line.bli_id);
                    const unit = bli ? getUnit(bli.unit_id) : null;
                    const total = line.qty && line.unit_price ? 
                      (parseFloat(line.qty) * parseFloat(line.unit_price)).toFixed(2) : '';
                    
                    return (
                      <TableRow key={line.dc_boq_line_id}>
                        <TableCell>{line.line_no}</TableCell>
                        <TableCell>{bli?.item_code || ''}</TableCell>
                        <TableCell>{bli?.item_description || 'Unknown Item'}</TableCell>
                        <TableCell>{unit?.unit_code || ''}</TableCell>
                        <TableCell align="right">{line.qty}</TableCell>
                        <TableCell align="right">
                          {line.unit_price ? `$${parseFloat(line.unit_price).toFixed(2)}` : ''}
                        </TableCell>
                        <TableCell align="right">
                          {total ? `$${total}` : ''}
                        </TableCell>
                        <TableCell>{line.notes}</TableCell>
                      </TableRow>
                    );
                  })}
                  <TableRow>
                    <TableCell colSpan={6} align="right" sx={{ fontWeight: 'bold' }}>
                      Total:
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                      ${dcBoqLines
                        .filter(line => line.qty && line.unit_price)
                        .reduce((sum, line) => sum + (parseFloat(line.qty) * parseFloat(line.unit_price)), 0)
                        .toFixed(2)}
                    </TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Compile BOQ Dialog */}
      <Dialog open={compileDialogOpen} onClose={() => setCompileDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Compile BOQ to Contract
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            This will compile BOQ lines from Design Change <strong>{selectedDC}</strong> into 
            Contract <strong>{getContractName(selectedContract)}</strong>.
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            This action will create new contract budget lines. Make sure this is intended.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCompileDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={() => handleCompileDC(selectedContract, selectedDC)}
            variant="contained"
            color="primary"
          >
            Compile BOQ
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !deleting && setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" fontWeight={600}>
            Confirm Delete
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 1 }}>
            Are you sure you want to delete this DC-Contract relationship?
          </Typography>
          {itemToDelete && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Contract: {itemToDelete.contract_title || itemToDelete.contract_number}
              <br />
              Design Change: {itemToDelete.change_name || itemToDelete.change_number}
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary">
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            disabled={deleting}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon />}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={() => setNotification({ ...notification, open: false })}
      >
        <Alert
          onClose={() => setNotification({ ...notification, open: false })}
          severity={notification.severity as AlertColor}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>

      {/* Import Results Dialog */}
      <Dialog
        open={importDialogOpen}
        onClose={handleImportDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ borderBottom: 1, borderColor: 'divider', pb: 2 }}>
          <Typography variant="h6" fontWeight={600}>
            Import Results
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {importResults && (
            <>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body1" gutterBottom>
                  <strong>Created:</strong> {importResults.created || 0}
                </Typography>
                <Typography variant="body1" gutterBottom>
                  <strong>Updated:</strong> {importResults.updated || 0}
                </Typography>
                <Typography variant="body1" gutterBottom>
                  <strong>Errors:</strong> {importResults.total_errors || 0}
                </Typography>
              </Box>

              {importResults.errors && importResults.errors.length > 0 && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    Import Errors:
                  </Typography>
                  <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                    {importResults.errors.map((err: any, idx: number) => (
                      <Typography key={idx} variant="body2" sx={{ mt: 0.5 }}>
                        Row {err.row}: {err.field} - {err.error}
                      </Typography>
                    ))}
                  </Box>
                </Alert>
              )}

              {(!importResults.errors || importResults.errors.length === 0) && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  All contract-design change links imported successfully!
                </Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider' }}>
          <Button onClick={handleImportDialogClose} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete All Confirmation Dialog */}
      <Dialog
        open={deleteAllDialogOpen}
        onClose={handleDeleteAllCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ borderBottom: 1, borderColor: 'divider', pb: 2 }}>
          <Typography variant="h6" fontWeight={600} color="error">
            Delete All Contract-Design Change Links
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This action cannot be undone!
          </Alert>
          <Typography variant="body1">
            Are you sure you want to delete <strong>ALL</strong> contract-design-change links for this project?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            This will permanently remove all links and their associated budget lines from the database.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider' }}>
          <Button onClick={handleDeleteAllCancel} variant="outlined" disabled={deletingAll}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteAllConfirm}
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            disabled={deletingAll}
          >
            {deletingAll ? 'Deleting...' : 'Delete All'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ContractDesignChanges;
